import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import cx from 'classnames';
import ResizableField from '../../../utility/resizableField';
import { GridYValue } from '../../../constants';
import { hasFloatingToolbar, legacyTextToolbarShift } from '../../../utility/textFieldLayout';
import { EditorToolbarToggle } from './editor-toolbar-toggle';
import { useDebouncedCallback } from 'use-debounce';

import 'react-quill/dist/quill.snow.css';

const Font = Quill.import('formats/font');
Font.whitelist = ['arial', 'times-new-roman'];
Quill.register(Font, true);

export const TEXT_ELEMENT_MODULES = {
  toolbar: [
    [{ font: ['arial', 'times-new-roman'] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ color: ['black', 'grey', 'silver', 'red', 'maroon', 'lime', 'green', 'aqua', 'blue', 'navy', 'orange', 'yellow', 'fuchsia'] }],
    ['bold', 'italic', 'underline'],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '+1' }, { indent: '-1' }],
    ['clean'],
  ],
};
export const TEXT_ELEMENT_FORMATS = ['font', 'size', 'color', 'bold', 'italic', 'underline', 'list', 'bullet', 'indent', 'align'];
const NBSP = '\u00A0';

// Pasted text often carries a run of blank lines at the end. They add nothing but make the
// field grow to fit them, so drop everything after the last line that has real content.
const trimTrailingEmptyLines = (editor) => {
  const text = editor.getText();
  // Quill always keeps a trailing newline of its own, which must stay.
  const end = Math.max(0, text.length - 1);

  let lastContent = end - 1;
  while (lastContent >= 0 && /[\s\u00A0\uFEFF]/.test(text[lastContent])) {
    lastContent -= 1;
  }

  // Keep the newline that closes the last line with content, drop every one after it.
  let keepTo = text.indexOf('\n', lastContent + 1);
  if (keepTo === -1 || keepTo > end) {
    keepTo = end;
  }

  if (keepTo < end) {
    editor.deleteText(keepTo, end - keepTo, 'user');
  }
};

// What a field's footprint holds on top of its writing area, for the fields that carry no
// room of their own (see `getElementOptionsByType`: height 28, innerElemHeight 19).
const DEFAULT_HEIGHT_MARGIN = 9;
// The shortest the writing area and the whole field are allowed to get, in grid rows. A
// single row, only so the field keeps a box at all: a line of text takes about 5 rows, and
// the 11 this used to be held a short text in a box twice its height. Dragged shorter than
// its text, the field scrolls, as it does at any height.
const MIN_INNER_ELEM_HEIGHT = 1;
const MIN_HEIGHT = MIN_INNER_ELEM_HEIGHT + DEFAULT_HEIGHT_MARGIN;

// Height the field needs to show the whole text without scrolling: the text itself plus
// the border of the box around it, which the editor's own scrollHeight leaves out.
//
// The editor is stretched to the height of the field and scrolls inside it, so its
// scrollHeight never reports less than the box it sits in: a text that has outgrown the
// box measures right, but one that no longer fills it measures as the box, and the field
// could only ever grow. Collapsing the editor first makes scrollHeight the height of the
// text alone, so the field follows the text down as well as up.
const getRequiredEditorHeight = (editor) => {
  const editorElem = editor.root;
  const containerElem = editorElem.parentNode;
  const borders = containerElem ? containerElem.offsetHeight - containerElem.clientHeight : 0;

  const ownHeight = editorElem.style.height;
  editorElem.style.height = '0px';
  const textHeight = editorElem.scrollHeight;
  editorElem.style.height = ownHeight;

  return textHeight + Math.max(0, borders);
};

export const TextElement = function FromElement({
  element,
  handlerChangeOption,
  setPosition,
  innerElemHeight,
  autoFillingVariables,
  addAutoFillingVariable,
}) {
  const quillRef = useRef(null);

  // The constructor opens a form with its text fields already on the floating-toolbar
  // layout (utility/textFieldLayout), bar the ones the filled form still draws with a
  // toolbar of their own - the ones the initiator may edit. Those keep the layout they
  // were authored with here too, so the canvas shows their text where the filled form
  // and the print put it.
  const isFloatingToolbar = hasFloatingToolbar(element);

  const [value, setValue] = useState(element.options.value);
  const [showEditor, setShowEditor] = useState(true);
  // The show/hide toggle is rendered into Quill's own container so it always sits on the
  // first line of the text, whatever height the toolbar happens to be.
  const [editorContainer, setEditorContainer] = useState(null);

  useEffect(() => {
    if (!isFloatingToolbar) return;

    setEditorContainer(quillRef.current?.getEditor()?.root?.parentNode ?? null);
  }, [isFloatingToolbar]);

  useEffect(() => {
    setValue(element.options.value);
  }, [element.options.value]);

  useEffect(() => {
    const editor = quillRef.current.getEditor();
    const handleSpace = (event) => {
      if (event.key === ' ') {
        event.preventDefault();
        const range = editor.getSelection();
        if (range) {
          editor.insertText(range.index, NBSP);
          editor.setSelection(range.index + 1);
          const newValue = editor.root.innerHTML;
          setValue(newValue);
          handlerChangeOption(newValue, 'value', element.id);
        }
      }
    };

    editor.root.addEventListener('keydown', handleSpace);
    return () => editor.root.removeEventListener('keydown', handleSpace);
  }, []);

  const handlerWidthChange = (shift) => {
    var formAreaWidth = document.getElementsByClassName('form-area')[0].offsetWidth;

    setPosition((prevPosition) => {
      return {
        ...prevPosition,
        width: Math.max(
          5,
          Math.min(100 - prevPosition.left, Math.floor((((prevPosition.startWidth / 100) * formAreaWidth + shift) / formAreaWidth) * 100)),
        ),
      };
    });
  };

  const finishWidthChange = () => {
    setPosition((prevPosition) => {
      return {
        ...prevPosition,
        startWidth: prevPosition.width,
      };
    });
  };

  const handlerHeightChange = (shift) => {
    setPosition((prevPosition) => {
      const diff = Math.floor(shift / GridYValue);

      return {
        ...prevPosition,
        height: Math.max(MIN_HEIGHT, prevPosition.startHeight + diff),
        innerElemHeight: Math.max(MIN_INNER_ELEM_HEIGHT, prevPosition.startInnerElemHeight + diff),
      };
    });
  };

  const finishHeightChange = () => {
    setPosition((prevPosition) => {
      return {
        ...prevPosition,
        startHeight: prevPosition.height,
        startInnerElemHeight: prevPosition.innerElemHeight,
      };
    });
  };

  // Writing the fitted height back to the form re-renders the whole canvas, so it waits
  // for the resize to settle instead of running on every step of a drag. The height on the
  // canvas is already up to date by then; this is what makes it stick.
  const commitFittedHeight = useDebouncedCallback(() => {
    setPosition((prevPosition) => ({
      ...prevPosition,
      startHeight: prevPosition.height,
      startInnerElemHeight: prevPosition.innerElemHeight,
    }));
  }, 200);

  // A text field's height belongs to its text: the same words need more rows in a narrow
  // field than in a wide one, so whenever the text rewraps the box is measured again and
  // the field is grown or shrunk to hold exactly what is in it.
  const fitHeightToText = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const editorHeight = getRequiredEditorHeight(editor);
    // Nothing to go on - the field is not laid out yet (or is being measured off-screen).
    if (!editorHeight) return;

    // A field that still holds its toolbar inside the writing area has to keep the rows
    // the toolbar takes on top of the text's own (the .quill-lg/md/sm/xs heights in
    // editor.scss); with the floating toolbar the text has the area to itself.
    const toolbarRows = isFloatingToolbar ? 0 : legacyTextToolbarShift(quillRef.current?.editingArea?.offsetWidth) ?? 4.2;
    const rows = Math.ceil(editorHeight / GridYValue + toolbarRows);

    setPosition((prevPosition) => {
      // Whatever the field's footprint holds besides the writing area stays as it is, so
      // only the text's own height moves. Fields authored before the floating toolbar
      // keep more room here than the current ones do (see utility/textFieldLayout).
      const heightMargin = Math.max(0, (prevPosition.height ?? 0) - (prevPosition.innerElemHeight ?? 0)) || DEFAULT_HEIGHT_MARGIN;
      const fittedInnerElemHeight = Math.max(MIN_INNER_ELEM_HEIGHT, rows);

      return {
        ...prevPosition,
        height: Math.max(MIN_HEIGHT, fittedInnerElemHeight + heightMargin),
        innerElemHeight: fittedInnerElemHeight,
      };
    });

    commitFittedHeight();
  }, [commitFittedHeight, isFloatingToolbar, setPosition]);

  // The text is re-measured off the field's rendered width rather than off the resize
  // handler, because the new width reaches the editor - and the text rewraps in it - only
  // once the field has been re-rendered. Watching the editor also covers a width changed
  // from anywhere else, not just by dragging the handle.
  useEffect(() => {
    const editorElem = quillRef.current?.getEditor()?.root;

    if (!editorElem || typeof ResizeObserver === 'undefined') return undefined;

    let lastWidth = null;
    let pendingFit = null;

    // The whole editor - toolbar, border and all - is as wide as the field.
    const fieldElem = editorElem.closest('.quill') ?? editorElem;

    const observer = new ResizeObserver(() => {
      // The width of the field, not the width the text wraps at: Quill gives the box a
      // scrollbar as soon as the text outgrows it, and that scrollbar takes 15px off the
      // content width without the field getting any narrower. Fitting on that made the
      // field grow back to its text every time it was dragged shorter than the text -
      // the box shrank, the scrollbar appeared, the fit grew the box, the scrollbar went,
      // and the next step of the drag started over - so the field could not be made
      // shorter at all. Nor the width of the editor's own box: hiding the toolbar drops
      // the border around it, which widens it by 2px, and that snapped the field to the
      // height of its text whenever the toolbar was hidden or shown.
      const width = fieldElem.offsetWidth;

      // A hidden field measures 0 and tells us nothing; keep the width it had.
      if (!width || width === lastWidth) return;

      const isFirstMeasure = lastWidth === null;
      lastWidth = width;

      // The first measurement is the width the field is opened with, not a resize:
      // fitting there would rewrite the stored height of every text field on load.
      if (isFirstMeasure) return;

      // Measuring and resizing from inside the callback would resize the very element
      // being watched, in the frame it is being reported in - what browsers complain
      // about as a ResizeObserver loop. Leave that frame first.
      if (pendingFit !== null) cancelAnimationFrame(pendingFit);
      pendingFit = requestAnimationFrame(() => {
        pendingFit = null;
        fitHeightToText();
      });
    });

    observer.observe(editorElem);

    return () => {
      if (pendingFit !== null) cancelAnimationFrame(pendingFit);
      observer.disconnect();
    };
  }, [fitHeightToText]);

  useEffect(() => {
    if (quillRef.current.editingArea) {
      const elem = quillRef.current.editingArea.closest('.quill');
      ResizableField(elem, handlerWidthChange, finishWidthChange, handlerHeightChange, finishHeightChange);
    }
  }, [quillRef, element, element.options.width]);

  const debouncedUpdateVariables = useDebouncedCallback((e) => {
    const matches = [...e.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);

    const matchesToCreate = [];
    if (matches.length) {
      for (const match of matches) {
        if (!autoFillingVariables.find((variable) => variable.name.toLowerCase() === match.toLowerCase())) {
          matchesToCreate.push(match);
        }
      }
    }

    addAutoFillingVariable(matchesToCreate);
  }, 2500);

  const handelEdit = (e) => {
    setValue(e);
    handlerChangeOption(e, 'value', element.id);
    if (!quillRef.current) return;
    debouncedUpdateVariables(quillRef.current.editor.getText());
  };

  const getClassName = () => {
    const editorWidth = quillRef.current?.editingArea?.offsetWidth;

    if (!editorWidth || editorWidth >= 661) {
      return 'quill-lg';
    }

    if (editorWidth >= 343) {
      return 'quill-md';
    }

    if (editorWidth >= 272) {
      return 'quill-sm';
    }

    if (editorWidth >= 200) {
      return 'quill-xs';
    }

    return 'quill-lg';
  };

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    const editorElem = editor?.root;

    if (!editorElem) return undefined;

    const handlePaste = () => {
      setTimeout(() => {
        trimTrailingEmptyLines(editor);
        // The toolbar floats above the field instead of taking room inside it, so the
        // pasted text needs no allowance for its height.
        fitHeightToText();
      }, 300);
    };

    editorElem.addEventListener('paste', handlePaste);

    return () => editorElem.removeEventListener('paste', handlePaste);
  }, [fitHeightToText]);

  return (
    <div className={cx({ 'fb-text-element': isFloatingToolbar })}>
      {isFloatingToolbar ? (
        <EditorToolbarToggle container={editorContainer} isToolbarShown={showEditor} onToggle={() => setShowEditor(!showEditor)} />
      ) : (
        <div>
          <button className="btn-link mb-4 ml-auto" onClick={() => setShowEditor(!showEditor)}>
            {!showEditor && 'Show Editor'}
            {showEditor && 'Hide Editor'}
          </button>
        </div>
      )}
      <div className="d-flex input-resizable-wrapper flex-wrap" style={{ height: innerElemHeight }}>
        <ReactQuill
          theme="snow"
          ref={quillRef}
          value={value}
          style={{
            height: '100%',
            width: '100%',
          }}
          modules={TEXT_ELEMENT_MODULES}
          formats={TEXT_ELEMENT_FORMATS}
          className={cx(getClassName(), { hidden: !showEditor })}
          onChange={handelEdit}
        />
      </div>
    </div>
  );
};
